import React, { useState, useRef, useEffect, memo, useCallback } from 'react';

/**
 * DateCell - Date picker cell with auto-save
 * 
 * Props:
 * - value: Current date value (string in YYYY-MM-DD or ISO format)
 * - rowIndex: Index of the row in the data array
 * - rowId: Unique ID of the row (for saving)
 * - columnId: ID of the column
 * - hasEditPermission: Whether the user can edit this cell
 * - onUpdate: Callback to update local data (rowIndex, columnId, value)
 * - onSave: Callback to save to backend (rowId, columnId, oldValue, newValue) -> Promise
 * - isDragging: Whether drag-fill is currently active
 * - onDragFillStart: Callback to start drag-fill (e, rowIndex, columnId, value)
 * - showDragHandle: Whether to show the drag-fill handle (default: true)
 * - displayFormat: Format for display ('MM/DD/YYYY' or 'YYYY-MM-DD', default: 'MM/DD/YYYY')
 * - isRetrying: Whether the save is being retried due to lock conflict
 */
const DateCell = memo(({ 
  value, 
  rowIndex,
  rowId,
  columnId,
  hasEditPermission = false,
  onUpdate,
  onSave,
  isDragging = false,
  onDragFillStart,
  showDragHandle = true,
  displayFormat = 'MM/DD/YYYY',
  isRetrying = false,
  showTimestamp = false
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const dateInputRef = useRef(null);

  // Format date for display (MM/DD/YYYY or with timestamp)
  const formatDateForDisplay = useCallback((dateStr) => {
    if (!dateStr) return '';

    if (showTimestamp && dateStr.includes('T')) {
      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) {
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        const yyyy = d.getFullYear();
        const hh = String(d.getHours()).padStart(2, '0');
        const min = String(d.getMinutes()).padStart(2, '0');
        return `${mm}/${dd}/${yyyy} ${hh}:${min}`;
      }
    }

    // Parse date string manually to avoid timezone issues
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length !== 3) return dateStr;
    const [year, month, day] = parts;
    if (!year || !month || !day) return dateStr;
    
    if (displayFormat === 'YYYY-MM-DD') {
      return `${year}-${month}-${day}`;
    }
    return `${month}/${day}/${year}`;
  }, [displayFormat, showTimestamp]);

  // Format date for input field (YYYY-MM-DD)
  const formatDateForInput = useCallback((dateStr) => {
    if (!dateStr) return '';
    // Extract just the date part (YYYY-MM-DD) from any date string
    const datePart = dateStr.split('T')[0]; // Handle ISO strings with time
    // Validate it's in the correct format
    const parts = datePart.split('-');
    if (parts.length !== 3) return '';
    return datePart;
  }, []);

  // Open date picker when entering edit mode
  useEffect(() => {
    if (isEditing && dateInputRef.current) {
      // Small delay to ensure the input is rendered and focused
      setTimeout(() => {
        try {
          dateInputRef.current.showPicker();
        } catch (error) {
          // showPicker() may not be supported in all browsers, fail silently
          console.log('Date picker auto-open not supported in this browser');
        }
      }, 0);
    }
  }, [isEditing]);

  const handleClick = useCallback(() => {
    if (!isDragging && hasEditPermission && !isEditing) {
      setIsEditing(true);
      setEditValue(formatDateForInput(value));
    }
  }, [isDragging, hasEditPermission, isEditing, value, formatDateForInput]);

  const handleDateChange = useCallback((e) => {
    const newDate = e.target.value;
    setEditValue(newDate);
    
    const currentValue = formatDateForInput(value);
    // Auto-save when date is selected OR cleared
    if (newDate !== currentValue) {
      setIsEditing(false);
      
      const saveValue = newDate || null;
      
      if (onUpdate) {
        onUpdate(rowId, columnId, saveValue);
      }
      
      if (onSave) {
        setIsSaving(true);
        onSave(rowId, columnId, value, saveValue)
          .then(() => {
            setIsSaving(false);
          })
          .catch(error => {
            setIsSaving(false);
            console.error('Background save failed:', error);
          });
      }
    }
  }, [value, rowId, columnId, onUpdate, onSave, formatDateForInput]);

  const handleBlur = useCallback(() => {
    if (!dateInputRef.current) {
      setIsEditing(false);
      return;
    }
    const currentInputValue = dateInputRef.current.value;
    const currentValue = formatDateForInput(value);
    
    if (currentInputValue !== currentValue) {
      const saveValue = currentInputValue || null;
      if (onUpdate) onUpdate(rowId, columnId, saveValue);
      if (onSave) {
        setIsSaving(true);
        onSave(rowId, columnId, value, saveValue)
          .then(() => setIsSaving(false))
          .catch(error => { setIsSaving(false); console.error('Background save failed:', error); });
      }
    }
    setIsEditing(false);
  }, [value, rowId, columnId, onUpdate, onSave, formatDateForInput]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setEditValue(formatDateForInput(value));
      setIsEditing(false);
    }
  }, [value, formatDateForInput]);

  const handleDragStart = useCallback((e) => {
    if (onDragFillStart) {
      onDragFillStart(e, rowIndex, columnId, value);
    }
  }, [onDragFillStart, rowIndex, columnId, value]);

  if (isEditing) {
    return (
      <input
        ref={dateInputRef}
        type="date"
        value={editValue}
        onChange={handleDateChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        autoFocus
        style={{
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          padding: '2px 4px',
          border: '1px solid #007bff',
          borderRadius: '2px',
          fontSize: '11px',
          textAlign: 'center',
          boxSizing: 'border-box'
        }}
      />
    );
  }

  return (
    <div
      onClick={handleClick}
      style={{
        cursor: hasEditPermission ? 'pointer' : 'default',
        fontSize: '11px',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
        backgroundColor: 'transparent',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        userSelect: 'none',
        pointerEvents: isDragging ? 'none' : 'auto'
      }}
      title={hasEditPermission ? "Click to edit" : "View only - no edit permission"}
    >
      <span>
        {formatDateForDisplay(value) || '(empty)'}
      </span>
      {(isSaving || isRetrying) && (
        <div 
          style={{
            width: '10px',
            height: '10px',
            border: `2px solid ${isRetrying ? '#ffe4c4' : '#f3f3f3'}`,
            borderTop: `2px solid ${isRetrying ? '#ff8c00' : '#007bff'}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}
          title={isRetrying ? 'Retrying save...' : 'Saving...'}
        ></div>
      )}
      {/* Drag fill handle - consistent positioning across all cells */}
      {showDragHandle && hasEditPermission && onDragFillStart && (
        <div
          onMouseDown={handleDragStart}
          style={{
            position: 'absolute',
            bottom: '2px',
            right: '2px',
            width: '7px',
            height: '7px',
            backgroundColor: '#007bff',
            cursor: 'crosshair',
            borderRadius: '1px',
            zIndex: 10,
            boxShadow: '0 0 1px rgba(0,0,0,0.3)'
          }}
          title="Drag to fill cells"
        />
      )}
    </div>
  );
});

DateCell.displayName = 'DateCell';

export default DateCell;

